import { FC, KeyboardEvent, useState } from 'react';
import { CaretDownGreyIcon, GreyStrokeInfoIcon } from 'assets/icons';
import { TooltipWrapper } from 'components/tooltip/TooltipWrapper';
import { getAmountWithCurrency } from 'utils/amountUtils';
import {
  NextPayout,
  NextPayoutLayout,
  NextPayoutRow,
  NextPayoutRowStyle,
  PayoutDistributionItem,
  PayoutDistributionStatus
} from 'containers/payouts/payouts.model';
import { getPayoutDistributionLabel } from 'containers/payouts/utils';

const PAYOUT_DISTRIBUTION_TOOLTIP = 'Breakdown of your upcoming payouts by expected payout date.';

interface PayoutDistributionTimelineProps {
  nextPayout?: NextPayout | null;
  distribution?: PayoutDistributionItem[];
  onFooterClick?: (ctaType: string, ctaAction: string) => void;
}

const getRowLabelClassName = (style: string): string => {
  if (style === NextPayoutRowStyle.EMPHASIS) {
    return 'text-[20px] font-bold text-tertiaryText';
  }
  if (style === NextPayoutRowStyle.MUTED) {
    return 'text-sbase font-normal text-accentText';
  }
  return 'text-17px font-normal text-primaryText';
};

const getRowAmountClassName = (style: string): string => {
  if (style === NextPayoutRowStyle.EMPHASIS) {
    return 'text-[20px] font-bold text-tertiaryText';
  }
  if (style === NextPayoutRowStyle.MUTED) {
    return 'text-sbase font-normal text-accentText';
  }
  return 'text-17px font-normal text-tertiaryText';
};

const formatNextPayoutAmount = (amount: string): string => {
  const trimmedAmount = amount?.trim() ?? '';
  if (!trimmedAmount || Number.isNaN(Number(trimmedAmount))) {
    return trimmedAmount || '—';
  }
  return getAmountWithCurrency(+trimmedAmount);
};

const PayoutDistributionTimeline: FC<PayoutDistributionTimelineProps> = ({
  nextPayout,
  distribution,
  onFooterClick
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleToggleDistribution = () => {
    setIsExpanded(prev => !prev);
  };

  const handleFooterActivate = () => {
    const webCtaType = nextPayout?.footer?.webCtaType;
    const webCtaAction = nextPayout?.footer?.webCtaAction;
    if (!webCtaType || !webCtaAction) return;
    onFooterClick?.(webCtaType, webCtaAction);
  };

  const handleFooterKeyDown = (event: KeyboardEvent<HTMLDivElement | HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleFooterActivate();
    }
  };

  const renderFooter = () => {
    const footer = nextPayout?.footer;
    if (!footer?.label) return null;

    const hasWebCta = Boolean(footer.webCtaType && footer.webCtaAction);
    if (!hasWebCta) {
      return <div className="text-17px font-normal text-secondary">{footer.label}</div>;
    }

    return (
      <div
        className="flex cursor-pointer items-center text-17px font-normal text-secondary"
        role="button"
        tabIndex={0}
        aria-label={footer.label}
        onClick={handleFooterActivate}
        onKeyDown={handleFooterKeyDown}>
        {footer.label}
      </div>
    );
  };

  const renderListRow = (row: NextPayoutRow, index: number, isLast: boolean) => {
    const notes = row.notes?.filter(Boolean) ?? [];

    return (
      <div key={`${row.label}-${index}`}>
        <div className="flex items-start justify-between gap-3 py-2.5">
          <div className="flex min-w-0 flex-col gap-0.5">
            <div className={getRowLabelClassName(row.style)}>{row.label}</div>
            {notes.map(note => (
              <div key={note} className="text-sbase font-normal text-accentText">
                {note}
              </div>
            ))}
          </div>
          <div className={`shrink-0 ${getRowAmountClassName(row.style)}`}>{formatNextPayoutAmount(row.amount)}</div>
        </div>
        {!isLast ? <div className="h-px w-full bg-secondaryBtn" aria-hidden="true" /> : null}
      </div>
    );
  };

  if (!nextPayout) {
    if (!distribution?.length) return null;

    return (
      <div className="flex flex-col px-[30px]">
        <button
          type="button"
          className="flex w-full items-center justify-between"
          onClick={handleToggleDistribution}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Collapse payout distribution' : 'Expand payout distribution'}>
          <div className="relative flex items-center gap-1">
            <div className="text-sbase font-normal text-accentText">Expected payout distribution</div>
            <span
              className="inline-flex"
              onClick={event => event.stopPropagation()}
              onKeyDown={event => event.stopPropagation()}
              role="presentation">
              <TooltipWrapper
                tooltip={PAYOUT_DISTRIBUTION_TOOLTIP}
                position="bottom-left"
                allowMultilineTooltip
                className="!static w-fit shrink-0"
                customTooltipClassName="before:hidden z-50 !w-[220px] max-w-[calc(100vw-4rem)]">
                <GreyStrokeInfoIcon className="h-4 w-4" aria-label="Expected payout distribution information" />
              </TooltipWrapper>
            </span>
          </div>
          <CaretDownGreyIcon className={isExpanded ? 'rotate-180' : ''} aria-hidden="true" />
        </button>

        {isExpanded && (
          <div className="mt-5 flex flex-col">
            {distribution.map((item, index) => {
              const isLast = index === distribution.length - 1;
              const isUnscheduled = item.status === PayoutDistributionStatus.UNSCHEDULED;
              const label = getPayoutDistributionLabel(item.expectedBy, item.status);

              return (
                <div
                  key={`${item.status}-${item.expectedBy ?? 'unscheduled'}-${index}`}
                  className="flex items-start gap-3">
                  <div className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-borderGray" aria-hidden="true" />

                  <div className={`flex flex-1 items-start justify-between gap-3 ${isLast ? 'pb-1' : 'pb-3'}`}>
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <div className="text-17px font-semibold text-primaryText">{label}</div>
                      {isUnscheduled && (
                        <div className="text-sbase font-normal text-accentText">Payout date not assigned yet</div>
                      )}
                    </div>

                    <div className="flex shrink-0 flex-col items-end gap-0.5">
                      <div className="text-17px font-semibold text-tertiaryText">
                        {getAmountWithCurrency(+item.amount)}
                      </div>
                      {item.remainingAfter != null && +item.remainingAfter !== 0 && (
                        <div className="text-sbase font-normal text-accentText">
                          {getAmountWithCurrency(+item.remainingAfter)} left
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  const isListLayout = nextPayout.layout?.toUpperCase() === NextPayoutLayout.LIST;
  const info = nextPayout.info;
  const rows = nextPayout.rows ?? [];
  const hasInfoTooltip = Boolean(isListLayout && info?.title && info?.body);

  return (
    <div className="flex w-full flex-col gap-2 px-[30px]">
      <div className={`relative flex items-center gap-1 ${hasInfoTooltip ? 'group z-0 hover:z-50' : ''}`}>
        <div className="min-w-0 text-sbase font-normal text-accentText">{nextPayout.title}</div>
        {hasInfoTooltip ? (
          <>
            <GreyStrokeInfoIcon
              className="h-4 w-4 shrink-0 cursor-pointer"
              aria-label={info?.title}
              aria-describedby="next-payout-info-tooltip"
            />
            <div
              id="next-payout-info-tooltip"
              role="tooltip"
              className="absolute left-0 top-full z-50 hidden w-full max-w-full pt-2
                group-hover:block">
              <div
                className="rounded-md bg-white p-4 text-left
                  shadow-[0px_4px_14px_rgba(130,130,130,0.25)]">
                <div className="flex flex-col gap-0.5">
                  <div className="text-sbase font-semibold text-primaryText">{info?.title}</div>
                  <div className="text-px13 font-normal text-primaryText">{info?.body}</div>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>

      {isListLayout ? (
        <div className="flex flex-col">
          {rows.map((row, index) => renderListRow(row, index, index === rows.length - 1))}
        </div>
      ) : (
        nextPayout.body && <div className="text-17px font-normal text-primaryText">{nextPayout.body}</div>
      )}

      {renderFooter()}
    </div>
  );
};

export default PayoutDistributionTimeline;
